/**
 * Recurring discount applied to a single store of a subscription block.
 *
 * - `storeUuid`: store the discount belongs to.
 * - `amount`: recurring discount in MXN/month. `0` when the store has no discount.
 * - `expirationDate`: `YYYY-MM-DD` when the discount expires; `''` when it never expires
 *   or the backend does not expose it.
 */
export type BlockDiscountStore = Readonly<{
  storeUuid: string;
  amount: number;
  expirationDate: string;
}>;

/**
 * Aggregated recurring discounts of a subscription block.
 *
 * Read-only projection derived by the backend from every store's billing config.
 *
 * - `total`: sum of every store's recurring discount (MXN/month). Includes already
 *   expired discounts, so it is an "has a discount configured" signal, not a vigency one.
 * - `expirationDate`: only populated for INDIVIDUAL blocks; `''` for CONSOLIDATED
 *   blocks and for discounts without an expiration.
 * - `stores`: per-store breakdown.
 */
export type BlockDiscounts = Readonly<{
  total: number;
  expirationDate: string;
  stores: readonly BlockDiscountStore[];
}>;

/**
 * Normalizes a raw amount to a non-negative number.
 *
 * Non-numeric (including `NaN`/`Infinity`) and negative values degrade to `0` so a
 * corrupt backend value never turns into a bogus promotion.
 */
const normalizeAmount = (raw: unknown): number => {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return 0;
  return raw;
};

/**
 * Creates a validated BlockDiscounts value object.
 *
 * Never throws: every field degrades to a safe default (`total: 0`, `expirationDate: ''`,
 * `stores: []`) because the node is a read-only projection that must not break the render
 * of a subscription block.
 *
 * @param raw - Raw discounts data from infrastructure
 * @returns Frozen BlockDiscounts value object
 */
export const createBlockDiscounts = (raw: {
  total?: number | null;
  expirationDate?: string | null;
  stores?: { storeUuid: string; amount?: number | null; expirationDate?: string | null }[] | null;
}): BlockDiscounts =>
  Object.freeze({
    total: normalizeAmount(raw?.total),
    expirationDate: raw?.expirationDate ?? '',
    stores: Object.freeze(
      (raw?.stores ?? []).map((store) =>
        Object.freeze({
          storeUuid: store.storeUuid,
          amount: normalizeAmount(store.amount),
          expirationDate: store.expirationDate ?? '',
        }),
      ),
    ),
  });
